<template>
    <div class="container mx-auto px-3 pb-32 md:px-8" :class="{
        'space-y-8 md:space-y-12': editing,
        '2xl:max-w-(--breakpoint-xl)': !editing
    }" v-if="mounted && !loading">
        <page-header v-if="editing" class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a href="/automation/flows" class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Flows
                </a>
            </template>

            {{ playbook.name }}
        </page-header>

        <div class="grid grid-cols-12">
            <page-sidebar v-if="editing" :links="[
                    { href: `/automation/flows/${props.flow?.flow_id}`, label: 'Edit' },
                ]">
            </page-sidebar>

            <div class="col-span-12"
                  :class="{
                      'lg:col-span-10': editing,
                  }">
                <playbook-card>
                    <template v-slot:type>{{ playbookOptions.badge }}</template>
                    <template v-if="editing" v-slot:toggle>
                        <switch-flow-status :modelValue="playbook.status === 'active'" @update:modelValue="handleLaunch(props.flow)"></switch-flow-status>
                    </template>
                    <template v-slot:title>{{ playbookOptions.name }}</template>
                    <template v-slot:description>{{ playbookOptions.description }}</template>

                    <component :is="playbookOptions.art"></component>
                </playbook-card>

                <div class="max-w-screen-sm" :class="{
                    'mx-auto pt-8 lg:pt-14': !editing,
                    'pt-6': editing
                }">
                    <div v-if="!editing" class="text-2xl leading-tight font-extrabold mb-6">
                        Configure this Playbook
                    </div>

                    <div id="playbook-form">
                        <playbook-form-card :icon="faSliders">
                            <template v-slot:header>
                                Details
                            </template>

                            <div>
                                <vx-label size="lg">
                                    Flow Name
                                </vx-label>
                                <vx-input v-model="playbook.name"
                                    data-test="flow-name"
                                    :error="displayFirstError(validation.name.$errors)"
                                    size="lg"
                                    :maxlength="80"
                                    ></vx-input>
                            </div>
                            <div>
                                <vx-label size="lg">
                                    Notes
                                </vx-label>
                                <vx-input v-model="playbook.notes"
                                    data-test="flow-notes"
                                    :error="displayFirstError(validation.notes.$errors)"
                                    :rows="4"
                                    size="lg"
                                    :maxlength="2000"></vx-input>
                            </div>
                        </playbook-form-card>

                        <div>
                            <component :is="playbookOptions.config"
                                v-model="playbook.config"></component>
                        </div>


                        <vx-sticky-footer class="flex-col! mt-4 lg:flex lg:flex-row!">
                            <vx-button href="/automation/flows"
                                class="order-3 lg:w-20 lg:order-1"
                                size="lg"
                                color="muted-dark"
                                outline>
                                Cancel
                            </vx-button>
                            <template v-if="editing">
                                <vx-button @click="submit()"
                                    data-test="submit-update"
                                    :loading="processing"
                                    type="button"
                                    class="order-1 lg:grow lg:order-3"
                                    size="lg"
                                    :color="playbook.status === 'active' ? 'primary' : 'muted-dark'">
                                    Update
                                </vx-button>
                            </template>
                            <template v-else>
                                <vx-button @click="submit()"
                                    data-test="save-for-later"
                                    :loading="processing"
                                    type="button"
                                    class="order-2 lg:grow lg:order-2"
                                    size="lg"
                                    color="muted-dark">
                                    Save for Later
                                </vx-button>
                            </template>
                        </vx-sticky-footer>
                    </div>
                </div>
                    <playbook-launch
                        :visible="!!(statusModal.flow_id && statusModal.status === 'draft')"
                        @update:visible="() => statusModal = {}"
                        :flowId="statusModal.flow_id"
                        :flowStatus="statusModal.status"
                        :flowCreatedAt="statusModal.created_at"
                        @launched="() => {
                            playbook.status = 'active'; statusModal = {};
                            if (!props.flow) {
                                launched = true;
                            }
                        }"
                    >
                    </playbook-launch>
                    <playbook-pause
                        :visible="!!(statusModal.flow_id && statusModal.status === 'active')"
                        @update:visible="() => statusModal = {}"
                        :flowId="statusModal.flow_id"
                        @paused="playbook.status = 'paused'; statusModal = {};">
                    </playbook-pause>
            </div>
        </div>
    </div>
</template>

<script setup>
import SwitchFlowStatus from '../../components/SwitchFlowStatus.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import PlaybookCard from '../PlaybookCard.vue';
import { playbookGallery } from '../playbook';
import { faSliders, faArrowLeft, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import PlaybookFormCard from './PlaybookFormCard.vue';
import { VxLabel, VxInput, VxStickyFooter, VxButton } from '@voxie/frontend-components';
import { ref, watch, shallowRef, computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PlaybookLaunch from '../modals/PlaybookLaunch.vue';
import PlaybookPause from '../modals/PlaybookPause.vue';
import { flowsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { required } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';

const toasted = useToasted();

const props = defineProps({
    flow: {
        type: [Object, undefined],
        default: undefined,
    }
})

const mounted = ref(false);

const params = new URLSearchParams(window.location.search);
const teamId = Spark.state.currentTeam.id;

const editing = computed(() => Boolean(props.flow?.flow_id));

const playbook = ref({
    flow_id: undefined,
    name: '',
    notes: '',
    status: 'draft',
    type: '',
    config: {}
});


const playbookOptions = shallowRef(undefined);
const loading = ref(false);

const prepareNewForm = async () => {
    playbookOptions.value = playbookGallery.find(item => item.type === params.get('playbook'));

    if (!playbookOptions.value) {
        window.location.href = '/automation/flows';
    }

    playbook.value.notes = playbookOptions.value.description;
    playbook.value.config = playbookOptions.value.defaultConfig;
    playbook.value.type = playbookOptions.value.type;
}

const prepareEditForm = async () => {
    playbook.value.flow_id = props.flow.flow_id;
    playbook.value.name = props.flow.name;
    playbook.value.notes = props.flow.notes;
    playbook.value.status = props.flow.status;
    playbook.value.type = props.flow.playbook;

    playbookOptions.value = playbookGallery.find(item => item.type === playbook.value.type);

    const configResponse = await flowsClient.getConfig(teamId, props.flow.flow_id);

    playbook.value.config = configResponse.data.config;
}

const prepareForm = async () => {
    if (loading.value) {
        return
    }

    try {
        mounted.value = false;
        loading.value = true;

        if (!props.flow) {
            await prepareNewForm();
        } else {
            await prepareEditForm();
        }

        mounted.value = true;
    } catch (e) {
        console.error(e);

        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
}

watch(() => props.flow?.flow_id, async () => {
    prepareForm();
}, {
    immediate: true
});


const save = async () => {
    const { data } = await flowsClient.createOrUpdate(teamId, {
        flow_id: playbook.value.flow_id,
        name: playbook.value.name,
        notes: playbook.value.notes,
        playbook: playbook.value.type,
    });

    playbook.value.flow_id = data.flow_id;
    playbook.value.status = data.status;

    await flowsClient.saveConfig(teamId, playbook.value.flow_id, {
        config: playbook.value.config
    });
};

const { submit, processing, validation } = useFormSubmission({
    data: playbook,
    validation: {
        name: { required },
        notes: { required },
    },
    preserveScroll: () => editing.value,
    submit: save,
    reset: () => {
        if (!props.flow?.flow_id) {
            playbook.value = {
                flow_id: undefined,
                name: '',
                notes: '',
                status: 'draft',
                type: '',
                config: {}
            };
            prepareForm();
        }
    },
    notification: () =>
        editing.value
            ? {
                  message: 'Flow updated',
                  action: {
                      icon: faSquareArrowUpLeft,
                      label: 'Back to Flows',
                      href: '/automation/flows',
                  },
              }
            : {
                  message: 'We have saved your configuration but your flow is not launched yet',
                  redirect: '/automation/flows',
              },
});


const statusModal = ref({});
const launchingId = ref(undefined);

const unpause = async (flow) => {
    if (launchingId.value) {
        return;
    }

    launchingId.value = flow.flow_id;

    try {
        await flowsClient.unpause(teamId, flow.flow_id);
        playbook.value.status = 'active'
    } catch (e) {
        console.error(e);
        if (e?.response?.status === HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        const errorMessage = e.response?.data?.message;
        toasted.error(errorMessage || 'Something wrong.');
    } finally {
        launchingId.value = undefined;
    }
}

const handleLaunch = (flow) => {
    if (flow.status === 'draft' || flow.status === 'active') {
        statusModal.value = flow;
    } else if (flow.status === 'paused') {
        unpause(flow);
    }
}

</script>
